import React, { useState } from 'react';
import { useRoomStatus } from '@/hooks/useRoomStatus';
import { adminClient } from '@/api/adminClient';
import RoomStatusCard from '@/components/RoomStatusCard';
import ConnectionList from '@/components/ConnectionList';
import ErrorLogViewer from '@/components/ErrorLogViewer';
import type { ErrorLog, SystemHealth } from '@/types';

export default function Dashboard() {
  const { rooms, connections, metrics, loading, error, toggleRoom, disconnectUser, refresh } =
    useRoomStatus();

  const [errorLogs, setErrorLogs] = useState<ErrorLog[]>([]);
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'connections' | 'errors'>('overview');

  React.useEffect(() => {
    loadErrorLogs();
    loadSystemHealth();

    const interval = setInterval(() => {
      loadSystemHealth();
    }, 10000); // Update every 10 seconds

    return () => clearInterval(interval);
  }, []);

  const loadErrorLogs = async () => {
    try {
      const logs = await adminClient.getErrorLogs(50);
      setErrorLogs(logs);
    } catch (err) {
      console.error('Failed to load error logs:', err);
    }
  };

  const loadSystemHealth = async () => {
    try {
      const health = await adminClient.getSystemHealth();
      setSystemHealth(health);
    } catch (err) {
      console.error('Failed to load system health:', err);
    }
  };

  const handleClearLogs = async () => {
    try {
      await adminClient.clearErrorLogs();
      setErrorLogs([]);
    } catch (err) {
      console.error('Failed to clear logs:', err);
    }
  };

  const handleToggleRoom = async (roomId: string, enabled: boolean) => {
    try {
      await toggleRoom(roomId, enabled);
    } catch (err) {
      console.error('Failed to toggle room:', err);
    }
  };

  const handleDisconnectUser = async (connectionId: string) => {
    try {
      await disconnectUser(connectionId);
    } catch (err) {
      console.error('Failed to disconnect user:', err);
    }
  };

  // Calculate stats from rooms data (participantCount sum)
  const totalConnections = rooms.reduce((sum, room) => sum + (room.participantCount || 0), 0);
  const activeRooms = rooms.filter(room => room.isActive).length;

  // Calculate average latency from metrics
  const validMetrics = metrics.filter(m => m.averageLatency > 0);
  const averageLatency =
    validMetrics.length > 0
      ? Math.round(
          validMetrics.reduce((sum, m) => sum + m.averageLatency, 0) / validMetrics.length
        )
      : 0;

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.loadingText}>Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.error}>
        <p style={styles.errorText}>Error: {error}</p>
        <button onClick={refresh} style={styles.retryButton}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Dashboard</h1>
          <p style={styles.subtitle}>Real-time monitoring and management</p>
        </div>
        <button onClick={refresh} style={styles.refreshButton}>
          Refresh
        </button>
      </div>

      {/* System Health */}
      {systemHealth && (
        <div style={styles.healthBar}>
          <div style={styles.serviceStatus}>
            <span style={styles.serviceLabel}>Database</span>
            <span style={{
              ...styles.statusBadge,
              backgroundColor: getServiceColor(systemHealth.services.database),
            }}>
              {systemHealth.services.database}
            </span>
          </div>

          <div style={styles.serviceStatus}>
            <span style={styles.serviceLabel}>Cache</span>
            <span style={{
              ...styles.statusBadge,
              backgroundColor: getServiceColor(systemHealth.services.cache),
            }}>
              {systemHealth.services.cache}
            </span>
          </div>

          <div style={styles.serviceStatus}>
            <span style={styles.serviceLabel}>Translation</span>
            <span style={{
              ...styles.statusBadge,
              backgroundColor: getServiceColor(systemHealth.services.translation),
            }}>
              {systemHealth.services.translation}
            </span>
          </div>

          <div style={styles.serviceStatus}>
            <span style={styles.serviceLabel}>TTS</span>
            <span style={{
              ...styles.statusBadge,
              backgroundColor: getServiceColor(systemHealth.services.tts),
            }}>
              {systemHealth.services.tts}
            </span>
          </div>
        </div>
      )}

      {/* Overview Stats */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statIcon}>👥</div>
          <div>
            <div style={styles.statValue}>{totalConnections}</div>
            <div style={styles.statLabel}>Total Connections</div>
          </div>
        </div>

        <div style={styles.statCard}>
          <div style={styles.statIcon}>🚪</div>
          <div>
            <div style={styles.statValue}>{activeRooms} / {rooms.length}</div>
            <div style={styles.statLabel}>Active Rooms</div>
          </div>
        </div>

        <div style={styles.statCard}>
          <div style={styles.statIcon}>⚡</div>
          <div>
            <div style={styles.statValue}>{averageLatency}ms</div>
            <div style={styles.statLabel}>Avg Latency</div>
          </div>
        </div>

        <div style={styles.statCard}>
          <div style={styles.statIcon}>🚨</div>
          <div>
            <div style={styles.statValue}>
              {errorLogs.filter((log) => log.level === 'error' || log.level === 'critical').length}
            </div>
            <div style={styles.statLabel}>Active Errors</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        <button
          onClick={() => setActiveTab('overview')}
          style={{
            ...styles.tab,
            ...(activeTab === 'overview' ? styles.tabActive : {}),
          }}
        >
          Room Overview
        </button>
        <button
          onClick={() => setActiveTab('connections')}
          style={{
            ...styles.tab,
            ...(activeTab === 'connections' ? styles.tabActive : {}),
          }}
        >
          Connections ({connections.length})
        </button>
        <button
          onClick={() => setActiveTab('errors')}
          style={{
            ...styles.tab,
            ...(activeTab === 'errors' ? styles.tabActive : {}),
          }}
        >
          Error Logs ({errorLogs.length})
        </button>
      </div>

      {/* Tab Content */}
      <div style={styles.tabContent}>
        {activeTab === 'overview' && (
          <div style={styles.roomsGrid}>
            {rooms.map((room) => (
              <RoomStatusCard
                key={room.id}
                room={room}
                metrics={metrics.find((m) => m.roomId === room.id)}
                onToggle={(enabled) => handleToggleRoom(room.id, enabled)}
              />
            ))}
          </div>
        )}

        {activeTab === 'connections' && (
          <div style={styles.card}>
            <ConnectionList connections={connections} onDisconnect={handleDisconnectUser} />
          </div>
        )}

        {activeTab === 'errors' && (
          <div style={styles.card}>
            <ErrorLogViewer logs={errorLogs} onClear={handleClearLogs} />
          </div>
        )}
      </div>
    </div>
  );
}

function getServiceColor(status: 'operational' | 'degraded' | 'down'): string {
  if (status === 'operational') return '#48bb78';
  if (status === 'degraded') return '#ed8936';
  return '#f56565';
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px',
    maxWidth: '1600px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#1a202c',
    margin: '0 0 4px 0',
  },
  subtitle: {
    fontSize: '16px',
    color: '#718096',
    margin: 0,
  },
  refreshButton: {
    padding: '10px 20px',
    backgroundColor: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  healthBar: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
    marginBottom: '24px',
    padding: '20px',
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  serviceStatus: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  serviceLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#2d3748',
  },
  statusBadge: {
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600',
    color: 'white',
    textAlign: 'center',
    textTransform: 'capitalize',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  statCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '20px',
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  statIcon: {
    fontSize: '32px',
  },
  statValue: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#2d3748',
  },
  statLabel: {
    fontSize: '14px',
    color: '#718096',
  },
  tabs: {
    display: 'flex',
    gap: '8px',
    marginBottom: '24px',
    borderBottom: '2px solid #e2e8f0',
  },
  tab: {
    padding: '12px 24px',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    fontSize: '14px',
    fontWeight: '600',
    color: '#718096',
    cursor: 'pointer',
    marginBottom: '-2px',
  },
  tabActive: {
    color: '#667eea',
    borderBottomColor: '#667eea',
  },
  tabContent: {
    minHeight: '400px',
  },
  roomsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: '20px',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  loading: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    fontSize: '18px',
    color: '#4a5568',
  },
  error: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
  },
  errorText: {
    fontSize: '18px',
    color: '#c53030',
    margin: 0,
  },
  retryButton: {
    padding: '10px 20px',
    backgroundColor: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};
